/** KPI history (24 h / 7 days): one line, crosshair + tooltip on hover, min / max / latest as text. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../api.ts';
import { formatValue } from './ClusterPreview.tsx';

interface History { metricId: string; unit: string; hours: number; bucketMs: number; points: [number, number][] }

const RANGES = [24, 168] as const;
const H = 200;
const PAD = { top: 12, right: 12, bottom: 24, left: 52 };

/** Round tick values covering [min, max]. */
function ticks(min: number, max: number, count = 4) {
  const span = max - min || Math.abs(max) || 1;
  const raw = span / count;
  const p = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * p).find((s) => s >= raw)!;
  const lo = Math.floor(min / step) * step;
  let hi = Math.ceil(max / step) * step;
  if (hi === lo) hi = lo + step;
  const out: number[] = [];
  for (let v = lo; v <= hi + step / 2; v += step) out.push(Number(v.toPrecision(12)));
  return out;
}

function timeLabel(t: number, hours: number) {
  const d = new Date(t);
  return hours <= 24 ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : d.toLocaleDateString([], { weekday: 'short', day: 'numeric' });
}

export function HistoryChart({ metricId, name }: { metricId: string; name: string }) {
  const { t } = useTranslation();
  const [hours, setHours] = useState<number>(24);
  const [hover, setHover] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const q = useQuery({
    queryKey: ['history', metricId, hours],
    queryFn: () => api<History>(`/api/metrics/${metricId}/history?hours=${hours}`),
    refetchInterval: 60_000,
  });
  const plotRef = useRef<HTMLDivElement>(null);
  // Drawn at the real pixel width so axis text keeps its size on any screen.
  const [W, setW] = useState(600);
  useEffect(() => {
    const el = plotRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => e && setW(Math.max(280, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [!!q.data?.points.length]);

  const chart = useMemo(() => {
    const pts = q.data?.points ?? [];
    if (pts.length === 0) return null;
    const now = Date.now();
    const x0 = now - hours * 3600_000;
    const values = pts.map((p) => p[1]);
    const yTicks = ticks(Math.min(...values), Math.max(...values));
    const y0 = yTicks[0]!;
    const y1 = yTicks[yTicks.length - 1]!;
    const x = (tm: number) => PAD.left + ((tm - x0) / (now - x0)) * (W - PAD.left - PAD.right);
    const y = (v: number) => PAD.top + (1 - (v - y0) / (y1 - y0 || 1)) * (H - PAD.top - PAD.bottom);
    // Break the line where data is missing (more than 3 buckets apart).
    const gap = (q.data!.bucketMs ?? 0) * 3;
    const d = pts.map((p, i) => `${i === 0 || p[0] - pts[i - 1]![0] > gap ? 'M' : 'L'}${x(p[0]).toFixed(1)},${y(p[1]).toFixed(1)}`).join('');
    const xTicks = (W < 480 ? [0, 0.5, 1] : [0, 0.25, 0.5, 0.75, 1]).map((f) => x0 + f * (now - x0));
    return { pts, values, x, y, d, yTicks, xTicks };
  }, [q.data, hours, W]);

  function onMove(e: React.PointerEvent<SVGSVGElement>) {
    if (!chart || !svgRef.current) return;
    const r = svgRef.current.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    let best = 0;
    for (let i = 1; i < chart.pts.length; i++) if (Math.abs(chart.x(chart.pts[i]![0]) - px) < Math.abs(chart.x(chart.pts[best]![0]) - px)) best = i;
    setHover(best);
  }

  const unit = q.data?.unit ? ` ${q.data.unit}` : '';
  const hp = chart && hover !== null ? chart.pts[hover] : undefined;
  const latest = chart?.pts[chart.pts.length - 1];

  return (
    <section className="card stack" aria-labelledby={`hist-${metricId}`}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2 id={`hist-${metricId}`}>{t('history.title', { name })}</h2>
        <div className="segmented" role="group" aria-label={t('history.range')}>
          {RANGES.map((h) => (
            <button key={h} type="button" aria-pressed={hours === h} onClick={() => (setHours(h), setHover(null))}>{t(`history.range${h}`)}</button>
          ))}
        </div>
      </div>
      {q.isLoading && <p className="muted">{t('common.loading')}</p>}
      {q.isError && <p className="error">{t('common.error')}</p>}
      {q.data && !chart && <p className="muted">{t('history.empty')}</p>}
      {chart && (
        <>
          <div className="history-plot" ref={plotRef}>
            <svg
              ref={svgRef}
              viewBox={`0 0 ${W} ${H}`}
              className="history-svg"
              role="img"
              aria-label={t('history.summary', { name, min: formatValue(Math.min(...chart.values)), max: formatValue(Math.max(...chart.values)), latest: formatValue(latest![1]), unit })}
              onPointerMove={onMove}
              onPointerLeave={() => setHover(null)}
            >
              {chart.yTicks.map((v) => (
                <g key={v}>
                  <line x1={PAD.left} x2={W - PAD.right} y1={chart.y(v)} y2={chart.y(v)} className="history-grid" />
                  <text x={PAD.left - 6} y={chart.y(v)} className="history-axis" textAnchor="end" dominantBaseline="middle">{formatValue(v)}</text>
                </g>
              ))}
              {chart.xTicks.map((tm, i) => (
                <text key={tm} x={chart.x(tm)} y={H - 6} className="history-axis" textAnchor={i === 0 ? 'start' : i === chart.xTicks.length - 1 ? 'end' : 'middle'}>{timeLabel(tm, hours)}</text>
              ))}
              <path d={chart.d} className="history-line" />
              {hp && (
                <g>
                  <line x1={chart.x(hp[0])} x2={chart.x(hp[0])} y1={PAD.top} y2={H - PAD.bottom} className="history-crosshair" />
                  <circle cx={chart.x(hp[0])} cy={chart.y(hp[1])} r={4} className="history-dot" />
                </g>
              )}
              {/* Transparent hit area over the whole plot, larger than the line. */}
              <rect x={PAD.left} y={PAD.top} width={W - PAD.left - PAD.right} height={H - PAD.top - PAD.bottom} fill="transparent" />
            </svg>
            {hp && (
              <div className="history-tip" style={{ left: `${(chart.x(hp[0]) / W) * 100}%` }} role="status">
                <strong>{formatValue(hp[1])}{unit}</strong>
                <span>{new Date(hp[0]).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
              </div>
            )}
          </div>
          <dl className="history-stats">
            <div><dt>{t('history.latest')}</dt><dd>{formatValue(latest![1])}{unit}</dd></div>
            <div><dt>{t('history.min')}</dt><dd>{formatValue(Math.min(...chart.values))}{unit}</dd></div>
            <div><dt>{t('history.max')}</dt><dd>{formatValue(Math.max(...chart.values))}{unit}</dd></div>
          </dl>
        </>
      )}
    </section>
  );
}
